/**
 * Hook for accessing authentication state and actions
 * Wraps the Zustand auth store for easier usage in components
 */

import { useAuthStore } from '../store/authStore';
import type { User, LoginCredentials, RegisterData } from '../types';

/**
 * Authentication state and actions
 */
export interface UseAuthReturn {
  /** Current authenticated user (null if not authenticated) */
  user: User | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is being checked/loaded */
  isLoading: boolean;
  /** Authentication error message (if any) */
  error: string | null;
  /** Login with credentials */
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Register a new user */
  register: (data: RegisterData) => Promise<void>;
  /** Logout the current user */
  logout: () => Promise<void>;
  /** Fetch/refresh user data */
  fetchUser: () => Promise<void>;
  /** Clear any authentication errors */
  clearError: () => void;
  /** Check if user has admin role */
  isAdmin: boolean;
  /** Check if user account is disabled */
  isDisabled: boolean;
  /** Check if user email is verified */
  isEmailVerified: boolean;
}

/**
 * Hook for accessing authentication state and actions
 * @returns Authentication state and actions
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout, isAdmin } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <LoginForm onSubmit={login} />;
 * }
 *
 * return (
 *   <div>
 *     <h1>Welcome, {user.name}</h1>
 *     {isAdmin && <AdminPanel />}
 *     <button onClick={logout}>Logout</button>
 *   </div>
 * );
 * ```
 */
export function useAuth(): UseAuthReturn {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    fetchUser,
    clearError,
  } = useAuthStore();

  // Computed properties
  const isAdmin = user?.role === 'admin';
  const isDisabled = user?.disabled_at !== null;
  const isEmailVerified = user?.email_verified_at !== null;

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    fetchUser,
    clearError,
    isAdmin,
    isDisabled,
    isEmailVerified,
  };
}

export default useAuth;
